/**
 * ShipmentItemsSummary - Lista dos itens da remessa (criação e aba Itens).
 * Tabela compacta (tipo, identificação, local, quantidade, remover) com estado vazio
 * e botão "Adicionar itens" que abre o ShipmentItemsCatalogModal.
 *
 * RELEASE: 2026-10-16 v1.1.0 - Exibe situação e composição do kit.
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial (padrão Produtos e Serviços do Negócio).
 */
import { Package, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { shipmentPlaceText } from './ShipmentItemsCatalogModal';
import { ShipmentItemStatusLine } from './ShipmentItemStatus';
import type { ShipmentKitComponent } from '@/hooks/useShipments';

export const ITEM_LABEL: Record<string, string> = { tracker: 'Rastreador', chip: 'Chip', kit: 'Kit', product: 'Produto', free: 'Item livre' };

export interface SummaryRow {
  key: string;
  type: string;
  label: string;
  place: { type: string; label: string | null } | null;
  serial: boolean;
  quantity: number;
  /** Situação do equipamento e composição do kit (opcionais). */
  status?: string | null;
  components?: ShipmentKitComponent[] | null;
}

interface Props {
  rows: SummaryRow[];
  editable: boolean;
  onAdd?: () => void;
  onRemove?: (key: string) => void;
  removing?: boolean;
}

export function ShipmentItemsSummary({ rows, editable, onAdd, onRemove, removing }: Props) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-6 text-center">
        <Package className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Nenhum item na remessa.</p>
        {editable && <Button type="button" size="sm" variant="outline" onClick={onAdd}><Plus className="mr-1 h-4 w-4" />Adicionar itens</Button>}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-md border">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0">
            <Badge variant="secondary" className="shrink-0 text-[10px]">{ITEM_LABEL[r.type] ?? r.type}</Badge>
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{r.label}</p>
              {r.serial && <p className="truncate text-xs text-muted-foreground">{shipmentPlaceText(r.place)}</p>}
              {r.serial && <ShipmentItemStatusLine type={r.type} status={r.status} components={r.components} />}
            </div>
            <span className="shrink-0 tabular-nums text-muted-foreground">x{r.quantity}</span>
            {editable && (
              <Button type="button" size="icon" variant="ghost" className="h-8 w-8 shrink-0" aria-label="Remover item"
                disabled={removing} onClick={() => onRemove?.(r.key)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{rows.length} item(ns)</span>
        {editable && <Button type="button" size="sm" variant="outline" onClick={onAdd}><Plus className="mr-1 h-4 w-4" />Adicionar itens</Button>}
      </div>
    </div>
  );
}
